import { filter } from 'd3-array';
import axios from '../lib/axios';
import { showToast } from '../utils/toast';

// Helper function to format dates for the API
const formatDateForAPI = (dateString: string): string => {
  try {
    // Return if already in DD-MM-YYYY format
    if (/^\d{2}-\d{2}-\d{4}$/.test(dateString)) {
      return dateString;
    }

    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      throw new Error('Invalid date');
    }

    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  } catch (e) {
    console.error('Error formatting date:', e);
    return '';
  }
};

// Filter interface for orders
export interface OrderFilters {
  status?: OrderStatus | 'all';
  lender?: string;
  merchantId?: string;
  customerId?: string;
  dateRange?: {
    from?: string | undefined;
    to?: string | undefined;
  };
}

// Order Status type
export type OrderStatus = 'Verification Pending' | 'Completed';

export interface OrderApiResponse {
  status: string;
  result: {
    pagination: {
      totalCount: number;
      totalMatch: number;
      page: number;
      pageSize: number;
      totalPages: number;
    };
    data: Order[];
  };
}

// Updated Partner Order interface
export interface Order {
  _id: string;
  orderId: string;
  status: string;
  lender: string;
  amount: number;
  createdAt: string;
  updatedAt: string;
  subCategoy: string;
  utr: string | null;                // Add this
  utrReason: string | null;          // Add this
  utrUpdatedAt: number | null;       // Add this
  utrBy?: string;                    // Add this
  orderCreatedAt: number;       // Add this field
  settlement?: {
    customerName: string;
    customerMobileNo: string;
    productName: string;
    productVariation: string;
    merchantName: string;
    merchantMobileNo: string;
    merchantEmail: string;
    partnerName: string;
    bankAccountNo?: string;
    bankIfsc?: string;
    bankHolderName?: string;
    totalAmount: number;
    orderCreationAt?: string;
    orderLinkAt?: string;
    schemeName?: string;
    schemeDownPayment?: number;
    schemeTenure?: number;
    schemeEMIAmount?: number;
    paymentFrequency?: string;
    netDisbursement?: number;
    interestAmount?: number;
  };
  transaction?: {
    eligibleTenure: any[];
  };
  validation?: number;
  serialNo?: string;
  brandId?: string;
  productId?: string;
  variationString?: string;
  linkActivatedAt?: number;
  paymentLink?: string;
  redirectionLink?: string;
  docProduct?: string;
  docReceipt?: string;
  docProductAt?: string;
  docReceiptAt?: string;
  docVehicleChassis: string,
  docVehicleChassisAt: string,
  docVehicleMotor: string,
  docVehicleMotorAt: string,
  docRtoAcknowledgement: string,
  docRtoAcknowledgementAt: string,
  docInsurance: string,
  docInsuranceAt: string,
}

// Updated Partner Order interface to match the API response
export interface OrderDetail {
  _id: string;
  orderId: string;
  status: OrderStatus;  // Change to use OrderStatus type
  lender: string;
  amount: number;
  subCategory: string;
  utr: string | null;
  utrReason: string | null;
  utrUpdatedAt: string | null;  // Add this
  docProduct: string;
  docReceipt: string;
  docCustomerConsent: string,
  docCustomerConsentAt: string,
  docVehicleChassis: string,
  docVehicleChassisAt: string,
  docVehicleMotor: string,
  docVehicleMotorAt: string,
  docRtoAcknowledgement: string,
  docRtoAcknowledgementAt: string,
  docInsurance: string,
  docInsuranceAt: string,
  docProductAt: string;
  productDetails: string;
  docReceiptAt: string;
  createdAt: string;
  updatedAt: string;
  orderCreatedAt: number;
  linkActivatedAt: number;
  validation: number;
  serialNo: string;
  variationString: string;
  paymentLink: string;         // Add this
  redirectionLink: string;     // Add this

  // Nested objects
  partnerId: {
    _id: string;
    name: string;
    mobileNo: string;
    email: string;
    isEv: boolean;
  };

  merchantId: {
    _id: string;
    name: string;
    mobileNo: string;
    email: string;
    kycBank?: {
      accountNo: string;
      ifsc: string;
      holderName: string;
    };
  };

  customerId: {
    _id: string;
    name: string;
    mobileNo: string;
  };

  settlement: {
    customerName: string;
    customerMobileNo: string;
    productName: string;
    productVariation: string;
    merchantName: string;
    merchantMobileNo: string;
    merchantEmail: string;
    productDetails: string;
    partnerName: string;
    utr: string | null;
    utrAt: string | null;
    orderPaymentAt: string | null;
    orderId: string;
    orderCreationAt: string;
    orderLinkAt: string;
    schemeName: string;
    schemeDownPayment: number;
    schemeTenure: number;
    schemeEMIAmount: number;
    paymentFrequency: string;
    netDisbursement: number;
    totalAmount: number;
    interestAmount: number;
  };

  productId?: { // Change from null to undefined
    _id: string;
    partnerName: string;
    brandName: string;
    name: string;
    category: string;
    variations: Array<{
      name: string;
      mrp: number;
      mop: number;
      features: Array<{
        name: string;
        value: string;
      }>;
    }>;
  };

  keyFactStatement?: {
    amount: number;
    scheme: {
      name: string;
      grossTenure: number;
      advancedEMI: number;
      subventionCost: number;
      interestBased: boolean;
      annualInterest: number;
      otherCharges: number;
      totalProcessingFee: number;
      GST: number;
    };
    calculations: {
      name: string;
      grossTenure: number;
      NoOfNetEmis: number;
      netDisbursement: number;
      totalInterest: number;
      monthlyEMI: number;
      advancedEmiPayment: number;
      totalProcessingFee: number;
      DBDToDealer: number;
    };
    display: string;
  };

  // Add payment and transaction fields
  payment?: {
    amount?: number;
    status?: string;
    method?: string;
    details?: any;
  };

  transaction?: {
    eligibleTenure?: any[];
    transactionId?: string | null;
    transactionStatus?: string | boolean | null;
    transactionTime?: number | null;
    leafRefNo?: string | null;
  };
}



export interface OrdersResponse {
  status: string;
  result: {
    pagination: {
      totalCount: number;
      totalMatch: number;
      page: number;
      pageSize: number;
      totalPages: number;
    };
    data: Order[];
  };
}

export const fetchOrders = async ({
  page = 1,
  pageSize = 10,
  search = "",
  sortBy = "createdAt",
  sortOrder = "desc",
  lender= "",
  dateRange = { from: undefined, to: undefined },
  token = "",
  filters = {}
}: {
  page?: number;
  pageSize?: number;
  search?: string;
  sortBy?: string;
  orderId?: string;
  sortOrder?: string;
  lender?: string;
  filters?: OrderFilters;
  dateRange?: {
    from?: string;
    to?: string;
  };
  token?: string | null;
}): Promise<{
  orders: Order[];
  pagination: {
    totalCount: number;
    totalMatch: number;
    currentPage: number;
    pageSize: number;
    totalPages: number;
  };
}> => {
  try {
    const params = new URLSearchParams();
    // Convert to string and ensure it's a valid number
    params.append("page", Math.max(1, Number(page)).toString());
    params.append("pageSize", Math.max(1, Number(pageSize)).toString());
    params.append("sortBy", sortBy);
    params.append("sortOrder", sortOrder);

    if (search) params.append("search", search);

    // Format dates properly for the API
    if (dateRange.from) {
      const fromDate = formatDateForAPI(dateRange.from);
      if (fromDate) params.append("from", fromDate);
    }
    if (dateRange.to) {
      const toDate = formatDateForAPI(dateRange.to);
      if (toDate) params.append("to", toDate);
    }
    if (filters.status && filters.status !== 'all') {
      params.append("status", filters.status);
    }
   if (lender) {
    params.append("lender", lender.toLowerCase() === "caprion" ? "CAPRION" : lender);
}

    console.log('[API] Fetching orders with formatted params:', Object.fromEntries(params));

    const response = await axios.get<OrdersResponse>(`/lender-order?${params.toString()}`, {
      headers: {
        'Content-Type': 'application/json',
        'token': token || ''
      }
    });

    console.log('[API] Response:', response.data);

    const { pagination, data } = response.data.result;

    // Add serial numbers to orders
    const ordersWithSerialNo = data.map((order, index) => ({
      ...order,
      serialNo: ((pagination.page - 1) * pagination.pageSize + index + 1).toString()
    }));

    return {
      orders: ordersWithSerialNo,
      pagination: {
        totalCount: pagination.totalCount,
        totalMatch: pagination.totalMatch,
        currentPage: pagination.page,
        pageSize: pagination.pageSize,
        totalPages: pagination.totalPages
      }
    };
  } catch (error: any) {
    console.error('[API] Error fetching partner orders:', error);
    throw new Error('Failed to fetch partner orders');
  }
};

/**
 * Fetch partner order document (product or receipt)
 */
export const fetchOrderDocs = async (
  orderId: string,
  docType:  'docReceipt'  | 'docVehicleChassis' | 'docVehicleMotor' | 'docRtoAcknowledgement' | 'docInsurance' |'docProduct',
  token: string | null
): Promise<string> => {
  try {
    console.log(`[API] Fetching ${docType} for orderId:`, orderId);

    // Fix: Use the correct endpoint URL structure
    const endpoint = `/lender-order/${docType}?id=${orderId}`;
    console.log(`[API] Request URL:`, endpoint);

    const response = await axios.get<{
      status: string;
      result: { url: string };
    }>(endpoint, {
      headers: {
        'Content-Type': 'application/json',
        'token': token || localStorage.getItem("token") || ''
      }
    });

    console.log(`[API] ${docType} response:`, response.data);

    if (response.data?.status === "Request successful!" && response.data.result?.url) {
      const docUrl = response.data.result.url;
      console.log(`[API] Received ${docType} URL:`, docUrl);
      return docUrl;
    }

    throw new Error(`No URL found in response for ${docType}`);
  } catch (error: any) {
    console.error(`[API] Error fetching ${docType}:`, {
      error,
      message: error.message,
      response: error.response?.data
    });
    throw error.response?.data?.message || error.message || `Failed to fetch ${docType}`;
  }
};

/**
 * Fetch single partner order details
 */
export const fetchOrderById = async (
  orderId: string,
  lender: string,
  token: string | null
): Promise<OrderDetail> => {
  try {
    console.log(`[API] Fetching partner order details for orderId:`, orderId);

    const response = await axios.get<{
      status: string;
      result: {
        data: OrderDetail | OrderDetail[];
      };
    }>(`/lender-order?orderId=${orderId}&lender=${lender}`, {
      headers: {
        'Content-Type': 'application/json',
        'token': token || localStorage.getItem("token") || ''
      }
    });

    console.log('[API] Partner order detail response:', response.data);

    // Check if response has data
    if (response.data?.result?.data) {
      // Handle both array and single object responses
      const orderData = Array.isArray(response.data.result.data)
        ? response.data.result.data[0]
        : response.data.result.data;

      if (!orderData) {
        throw new Error('Order not found');
      }

      // Return processed data with defaults
      return {
        ...orderData,
        paymentLink: orderData.paymentLink || '',
        redirectionLink: orderData.redirectionLink || '',
        utrUpdatedAt: orderData.utrUpdatedAt || null,
        status: orderData.status as OrderStatus,
        productDetails: orderData.settlement?.productDetails || '',

        // Add payment and transaction with proper defaults
        payment: {
          amount: orderData.amount,
          status: orderData.status,
          ...orderData.payment
        },

        transaction: {
          eligibleTenure: orderData.transaction?.eligibleTenure || [],
          transactionId: orderData.transaction?.transactionId || null,
          transactionStatus: orderData.transaction?.transactionStatus || null,
          transactionTime: orderData.transaction?.transactionTime || null,
          leafRefNo: orderData.transaction?.leafRefNo || null
        },

        // Add nested objects with proper defaults
        partnerId: {
          _id: orderData.partnerId?._id || '',
          name: orderData.partnerId?.name || orderData.settlement?.partnerName || '',
          mobileNo: orderData.partnerId?.mobileNo || '',
          email: orderData.partnerId?.email || '',
          isEv: orderData.partnerId?.isEv || false,
        },
        merchantId: {
          _id: orderData.merchantId?._id || '',
          name: orderData.merchantId?.name || orderData.settlement?.merchantName || '',
          mobileNo: orderData.merchantId?.mobileNo || orderData.settlement?.merchantMobileNo || '',
          email: orderData.merchantId?.email || orderData.settlement?.merchantEmail || '',
          kycBank: orderData.merchantId?.kycBank || {
            accountNo: '',
            ifsc: '',
            holderName: ''
          }
        },
        customerId: {
          _id: orderData.customerId?._id || '',
          name: orderData.customerId?.name || orderData.settlement?.customerName || '',
          mobileNo: orderData.customerId?.mobileNo || orderData.settlement?.customerMobileNo || ''
        },
        // Ensure all required fields are present
        docProduct: orderData.docProduct || '',
        docReceipt: orderData.docReceipt || '',
        docProductAt: orderData.docProductAt || '',
        docReceiptAt: orderData.docReceiptAt || '',
        orderCreatedAt: orderData.orderCreatedAt || Date.now(),
        linkActivatedAt: orderData.linkActivatedAt || 0,
        validation: orderData.validation || 0,
        serialNo: orderData.serialNo || '',
        variationString: orderData.variationString || '',
        productId: orderData.productId || undefined,
        settlement: {
          customerName: orderData.settlement?.customerName || '',
          customerMobileNo: orderData.settlement?.customerMobileNo || '',
          productName: orderData.settlement?.productName || '',
          productVariation: orderData.settlement?.productVariation || '',
          merchantName: orderData.settlement?.merchantName || '',
          merchantMobileNo: orderData.settlement?.merchantMobileNo || '',
          merchantEmail: orderData.settlement?.merchantEmail || '',
          partnerName: orderData.settlement?.partnerName || '',
          productDetails: orderData.settlement?.productDetails || '',
          utr: orderData.settlement?.utr || null,
          utrAt: orderData.settlement?.utrAt || null,
          orderPaymentAt: orderData.settlement?.orderPaymentAt || null,
          orderId: orderData.settlement?.orderId || '',
          orderCreationAt: orderData.settlement?.orderCreationAt || '',
          orderLinkAt: orderData.settlement?.orderLinkAt || '',
          schemeName: orderData.settlement?.schemeName || '',
          schemeDownPayment: orderData.settlement?.schemeDownPayment || 0,
          schemeTenure: orderData.settlement?.schemeTenure || 0,
          schemeEMIAmount: orderData.settlement?.schemeEMIAmount || 0,
          paymentFrequency: orderData.settlement?.paymentFrequency || '',
          netDisbursement: orderData.settlement?.netDisbursement || 0,
          totalAmount: orderData.settlement?.totalAmount || 0,
          interestAmount: orderData.settlement?.interestAmount || 0
        }
      };
    }
    throw new Error('Invalid API response format');
  } catch (error: any) {
    console.error('[API] Error fetching partner order details:', error);
    throw error.response?.data?.message || error.message || 'Failed to fetch order details';
  }
};

